/**
 * RescheduleBookingPage - Page for rescheduling an existing booking.
 * Accessed via /booking/:cancelToken/reschedule
 *
 * Mirrors the main BookingPage layout — same three-column composition
 * (host context | calendar | slots), same BookingHostCard, same TZ
 * picker, same time-format toggle — so the booker rebooks with the
 * exact UX they used the first time, just preloaded with the meeting
 * they're moving.
 */

import { CheckCircle, RefreshCcw01 } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { BookingCalendar } from '@/components/scheduling/BookingCalendar';
import { BookingErrorBoundary } from '@/components/scheduling/BookingErrorBoundary';
import { BookingPageSkeleton } from '@/components/scheduling/BookingPageSkeleton';
import { getRescheduleDetails, rescheduleBooking } from '@/lib/api/scheduling';
import type {
  AvailabilityResponse,
  AvailableSlot,
  BookingSummary,
  BookingTypeSummary,
  PublicBookingType,
} from '@/lib/api/types/scheduling';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { logger } from '@/lib/logger';
import { BookingHostCard } from '@/lib/scheduling/components/BookingHostCard';
import { isMultiHostSchedulingType } from '@/lib/scheduling/hostIdentity';
import { useTimezone } from '@/lib/scheduling/useTimezone';

import { BookingLayout } from './BookingLayout';

type RescheduleState =
  | 'loading'
  | 'calendar'
  | 'submitting'
  | 'success'
  | 'error';

interface RescheduleDetails {
  booking: BookingSummary;
  bookingType: BookingTypeSummary;
  /**
   * First-window availability the reschedule endpoint pre-computes alongside
   * the booking metadata. We keep this around to seed BookingCalendar via
   * its `initialAvailability` prop so the picker renders immediately AND so
   * legacy reschedule links (where `bookingType.short_code` is missing) can
   * still show slots even when subsequent-month re-fetches aren't possible.
   */
  availability: AvailabilityResponse;
}

/**
 * Promote a sparse BookingTypeSummary to a PublicBookingType-shaped object
 * for BookingHostCard. Optional fields fall back to safe defaults so the
 * host card renders even on legacy / no-short-code rescheduling responses.
 */
function toPublicBookingType(summary: BookingTypeSummary): PublicBookingType {
  return {
    entity_id: summary.entity_id ?? '',
    short_code: summary.short_code ?? '',
    name: summary.name,
    display_name: summary.display_name ?? summary.name,
    description: summary.description ?? '',
    duration_minutes: summary.duration_minutes,
    buffer_before_minutes: summary.buffer_before_minutes,
    buffer_after_minutes: summary.buffer_after_minutes,
    location_type: summary.location_type ?? 'google_meet',
    owner_username: summary.owner_username ?? '',
    owner_display_name: summary.owner_display_name,
    owner_avatar_url: summary.owner_avatar_url ?? null,
    slug: summary.slug ?? '',
    global_slug: summary.global_slug ?? null,
    scheduling_type: summary.scheduling_type ?? 'individual',
    custom_questions: summary.custom_questions ?? [],
    hide_host_info: summary.hide_host_info ?? false,
  };
}

export function RescheduleBookingPage() {
  const { cancelToken } = useParams<{ cancelToken: string }>();
  const {
    timezone: selectedTimezone,
    setTimezone: setSelectedTimezone,
    availableTimezones,
  } = useTimezone();

  const [state, setState] = useState<RescheduleState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<RescheduleDetails | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Monotonic nonce that triggers a fetch refresh. Bumped by the
  // error-boundary reset handler so the booker can retry without a full
  // page reload — see `handleErrorReset` for the rationale.
  const [retryNonce, setRetryNonce] = useState(0);

  const handleErrorReset = useCallback(() => {
    // Drop UI state back to a clean loading skeleton AND kick the fetch
    // effect by bumping the retry nonce. Without the nonce bump, setting
    // `state='loading'` would render the skeleton indefinitely because the
    // fetch effect's dep array (cancelToken only) hasn't changed — the
    // user would be stuck on a spinner forever.
    setError(null);
    setSubmitError(null);
    setState('loading');
    setRetryNonce(n => n + 1);
  }, []);

  // Fetch booking details on mount / when token changes / on retry
  useEffect(() => {
    let cancelled = false;
    const fetchDetails = async () => {
      // Reset to a clean loading state at the START of every fetch — not
      // just on retry — so that swapping `cancelToken` (e.g. router replaces
      // the path with a new reschedule link without unmounting the page)
      // doesn't briefly show the PREVIOUS booking's details + host card +
      // calendar while the new fetch is in flight. Without this guard, the
      // booker would see stale data for one render tick before the new
      // details slot in, which reads as "wait, that's not my meeting".
      setState('loading');
      setError(null);
      setSubmitError(null);
      setDetails(null);

      if (!cancelToken) {
        setError('Invalid reschedule link');
        setState('error');
        return;
      }
      try {
        const data = await getRescheduleDetails(cancelToken, {
          timezone: selectedTimezone,
        });
        if (cancelled) {
          return;
        }
        setDetails({
          booking: data.booking,
          bookingType: data.booking_type,
          availability: data.availability,
        });
        setState('calendar');
      } catch (err) {
        if (cancelled) {
          return;
        }
        logger.error(
          'Error loading reschedule details',
          err instanceof Error ? err : new Error(String(err))
        );
        setError("We couldn't load this booking. Try again.");
        setState('error');
      }
    };
    void fetchDetails();
    return () => {
      cancelled = true;
    };
    // Don't include `selectedTimezone` — TZ changes route through
    // BookingCalendar's own availability re-fetch, not this top-level call.
    // `retryNonce` IS a dep so the error-boundary recovery path can force a
    // fresh fetch without unmounting / remounting the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelToken, retryNonce]);

  const handleSlotSelect = useCallback(
    async (slot: AvailableSlot) => {
      if (!cancelToken) {
        return;
      }
      setSubmitError(null);
      setState('submitting');
      try {
        await rescheduleBooking(cancelToken, {
          start_time: slot.start,
          end_time: slot.end,
          timezone: selectedTimezone,
        });
        setState('success');
      } catch (err) {
        logger.error(
          'Error rescheduling booking',
          err instanceof Error ? err : new Error(String(err))
        );
        setSubmitError("We couldn't reschedule this booking. Try again.");
        // Drop back to the calendar so the booker can try a different slot.
        setState('calendar');
      }
    },
    [cancelToken, selectedTimezone]
  );

  // Combined "moving from" label for the banner — date and time on one
  // line so the current booking reads as a single struck-through callout
  // above the picker. Buried in the host-card metadata it was too quiet:
  // bookers couldn't tell at a glance that this was a reschedule vs a
  // fresh booking. Promoted to a top-of-page banner instead.
  const movingFromLabel = useMemo(() => {
    if (!details?.booking) {
      return null;
    }
    const start = new Date(details.booking.start_time);
    const end = new Date(details.booking.end_time);
    const dateFmt = new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: selectedTimezone,
    });
    const timeFmt = new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: selectedTimezone,
    });
    return `${dateFmt.format(start)} · ${timeFmt.format(start)} – ${timeFmt.format(end)}`;
  }, [details, selectedTimezone]);

  const renderContent = () => {
    if (state === 'loading') {
      return <BookingPageSkeleton />;
    }

    if (state === 'error') {
      return (
        <div className='py-12 text-center'>
          <h1 className='mb-2 text-xl font-semibold text-[var(--color-text-primary)]'>
            Unable to Reschedule
          </h1>
          <p className='mx-auto max-w-md text-[var(--color-text-secondary)]'>
            {error || "We couldn't find this booking."}
          </p>
        </div>
      );
    }

    if (state === 'success') {
      return (
        <div className='mx-auto flex max-w-xl flex-col items-center gap-3 py-12 text-center'>
          <span className='inline-flex items-center gap-1.5 rounded-full bg-[var(--color-success-50)] px-2.5 py-1 text-xs font-medium text-[var(--color-text-success-primary)]'>
            <CheckCircle className='size-3.5' aria-hidden='true' />
            Rescheduled
          </span>
          <h1 className='text-2xl font-semibold leading-tight text-[var(--color-text-primary)]'>
            Meeting moved
          </h1>
          <p className='text-sm text-[var(--color-text-secondary)]'>
            Your meeting has been rescheduled. A confirmation email is on the
            way with the new details.
          </p>
        </div>
      );
    }

    // 'calendar' or 'submitting' — the picker. While submitting, dim
    // the calendar so the booker sees progress without losing context.
    if (!details) {
      return null;
    }
    const meetingType = toPublicBookingType(details.bookingType);
    // Legacy reschedule links may surface a sparse `booking_type` response
    // (the backend explicitly types this field as `Partial<PublicBookingType>`).
    // Prefer `short_code` — it's the canonical public identifier and survives
    // username changes — and fall back to `owner_username` + `slug` so
    // pre-short_code bookings still resolve. Only hard-fail when NEITHER
    // identifier is present AND the endpoint didn't pre-fetch availability;
    // without all three we genuinely cannot fetch the slot grid.
    const hasShortCode = !!meetingType.short_code;
    const hasUserSlug = !!(meetingType.owner_username && meetingType.slug);
    const canRefetchAvailability = hasShortCode || hasUserSlug;
    if (!canRefetchAvailability && !details.availability) {
      return (
        <div className='py-12 text-center text-[var(--color-text-secondary)]'>
          This booking can't be rescheduled through the link. Please contact the
          host directly.
        </div>
      );
    }
    // BookingCalendar requires a non-empty `meetingTypeId` string even when
    // it'll route through the username/slug path (the prop is unused on that
    // path but the TS contract is strict). Pass short_code when present,
    // otherwise pass the slug as a stable identifier so React keys stay
    // consistent if the calendar remounts.
    const calendarMeetingTypeId = hasShortCode
      ? meetingType.short_code
      : meetingType.slug || meetingType.entity_id || 'reschedule';

    // Individual bookings auto-detect the booker's timezone; multi-host
    // types expose the picker because availability spans multiple calendars.
    const isMultiHost = isMultiHostSchedulingType(meetingType.scheduling_type);

    return (
      <div className='flex flex-col gap-6'>
        {/* Reschedule banner — unambiguous signal that this is NOT a fresh
            booking. Sits above the picker so it's the first thing the
            booker reads. Amber/warning tint, badge + headline + "Moving
            from <strike>OLD TIME</strike>" pattern that visually marks
            the existing booking as going away. */}
        <div className='flex flex-col gap-3 rounded-xl border border-[var(--color-warning-200)] bg-[var(--color-warning-50)] px-4 py-3 sm:px-5 sm:py-4 md:flex-row md:items-center md:justify-between md:gap-4'>
          <div className='flex min-w-0 flex-col gap-1'>
            <span className='inline-flex w-fit items-center gap-1.5 rounded-full bg-[var(--color-warning-100)] px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-warning-primary)]'>
              <RefreshCcw01 className='size-3.5' aria-hidden='true' />
              Rescheduling
            </span>
            <p className='text-sm text-[var(--color-text-secondary)]'>
              Pick a new time for{' '}
              <span className='font-semibold text-[var(--color-text-primary)]'>
                {meetingType.display_name || meetingType.name}
              </span>
              .
            </p>
          </div>
          {movingFromLabel && (
            // "Moving from" stacks BELOW the heading on mobile so the badge
            // remains the first thing the booker reads. On tablet+ it sits
            // to the right and reads as a paired callout.
            <div className='flex flex-col gap-0.5 text-sm md:items-end md:text-right'>
              <span className='text-xs uppercase tracking-wide text-[var(--color-text-tertiary)]'>
                Moving from
              </span>
              <span className='font-medium text-[var(--color-text-secondary)] line-through decoration-[var(--color-text-tertiary)] decoration-1'>
                {movingFromLabel}
              </span>
            </div>
          )}
        </div>

        {/* Three-column grid mirrors BookingPage exactly so the rest of
            the page reads as the same product. Only the banner above
            distinguishes this as a reschedule. */}
        <div className='grid grid-cols-1 gap-6 md:grid-cols-[260px_minmax(0,1fr)] lg:gap-10'>
          <BookingHostCard
            meetingType={meetingType}
            timezone={selectedTimezone}
            onTimezoneChange={isMultiHost ? setSelectedTimezone : undefined}
            availableTimezones={isMultiHost ? availableTimezones : undefined}
            className='md:border-r md:border-[var(--color-border-secondary)] md:pr-6 lg:pr-10'
          />
          <div className='flex flex-col gap-4'>
            {submitError && (
              <div
                role='alert'
                className='rounded-lg border border-[var(--color-utility-error-300)] bg-[var(--color-utility-error-50)] p-4 text-sm text-[var(--color-text-error-primary)]'
              >
                {submitError}
              </div>
            )}
            <div
              className={
                state === 'submitting'
                  ? 'pointer-events-none opacity-60 transition-opacity'
                  : ''
              }
            >
              <BookingCalendar
                meetingTypeId={calendarMeetingTypeId}
                isShortCode={hasShortCode}
                username={hasShortCode ? undefined : meetingType.owner_username}
                slug={hasShortCode ? undefined : meetingType.slug}
                duration={meetingType.duration_minutes}
                timezone={selectedTimezone}
                onSlotSelect={handleSlotSelect}
                // Seed with the availability the reschedule endpoint
                // already pre-fetched. This means the picker renders
                // immediately (no second round-trip) AND legacy links
                // that can't drive subsequent-month fetches still get
                // the first window of slots to choose from.
                initialAvailability={details.availability}
              />
            </div>
            {state === 'submitting' && (
              <div className='flex items-center justify-center gap-2 text-sm text-[var(--color-text-secondary)]'>
                <LoadingIndicator size='sm' label='Rescheduling…' />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <BookingLayout>
      <BookingErrorBoundary onReset={handleErrorReset}>
        {renderContent()}
      </BookingErrorBoundary>
    </BookingLayout>
  );
}
